/**
 * DB테이블 노드 속성 — 연결 · 테이블 · 컬럼 · LOB 처리.
 *
 * 예전에는 JSON 스키마 폼(RJSF)으로 그려서 데이터처리 패널과 모양이 달랐고, 쓰이지 않는 칸이 섞여 있었다.
 * 현장 요청(2026-09-17)에 따라 다음을 정리했다.
 *
 *  - 모양: 데이터처리 패널과 같은 머리·칸·라벨(ui.tsx)
 *  - '데이터 정의' 칸: 예전에는 필수로 잡혀 '!' 판정만 흐려서 뺐다가, 정의↔노드 연결(정의를 고치면 노드에 퍼짐)을
 *    넣으면서 **선택** 칸으로 되살렸다 — 캔버스 '!' 가 '정의를 다시 고르라' 고 안내하므로 (DefinitionLinkSection).
 *  - Operation Mode / Write Mode 제거: 서버의 변환기(canvas_converter)는 DB테이블 노드의 이 값을 읽지 않는다.
 *    적재 방식은 데이터처리 노드에서 정한다. 이미 저장된 값은 건드리지 않고 화면에서만 뺐다.
 *  - 테이블 고르기: 칸에 이름을 치면 모든 스키마에서 찾아 드롭다운으로 보여 주고, '찾아보기' 팝업에서는
 *    이름 검색과 스키마별 목록을 함께 쓴다. 고르면 스키마와 테이블이 함께 정해진다.
 *  - 컬럼: 언제 읽었는지 보여 주고 '갱신' 으로 다시 읽는다. 바뀐 것이 있으면 기록해 두어, 이 테이블을
 *    소스·타깃으로 쓰는 데이터처리 노드가 점검에서 알려 준다.
 *  - 컬럼 상세: 데이터처리의 상세보기 팝업(항목 보기 / 값 보기)을 같이 쓴다.
 *  - LOB: 한 칸에 묶고 LOB 항목을 나열한다. LOB 항목이 없는 테이블이면 접힌 채 비활성.
 *
 * 연결을 골랐다고 첫 테이블을 멋대로 채우지 않는다 — 예전 폼의 자동 선택이 사용자가 고르지 않은 테이블을
 * 넣어 '!' 경고를 없애 버렸다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../../store';
import { fetchSchemas } from '../../../../store/connectionsSlice';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import { fetchTableColumns } from '../../../../utils/tableService';
import { nodeColumns, Column } from '../../utils/transformLoad';
import { diffColumns, hasChanges } from '../../utils/columnsDiff';
import DataDetailModal from './DataDetailModal';
import TableFinderModal, { FoundTable, searchTables } from './TableFinderModal';
import DefinitionLinkSection from './DefinitionLinkSection';
import { linkedDefinition } from '../../utils/definitionRefs';
import { Collapsible, FieldLabel, PanelHeader, Section, formatFetchedAt, inputCls, selectCls, smallBtn } from './ui';
import { isEnter } from '../../../../utils/keyboard';
import { useTranslation } from 'react-i18next';

interface Props {
  node: any;
  /** 최근 실행의 입력·출력 데이터 열기 — 상위 패널이 캔버스 변환까지 맡는다 */
  onOpenNodeData: () => void;
  nodeDataBusy: boolean;
}

/** LOB 로 볼 자료형 — 값이 커서 파일로 떨어뜨리거나 건너뛸 필요가 있는 것 */
const isLobType = (t?: string) => /lob|bfile|long|raw|bytea|binary|image|xml|json|text/i.test(t || '');

/** 표시 문구는 번역 키 — 모듈 상수라 렌더에서 t() 로 바꾼다 */
const LOB_MODES: Array<{ value: string; labelKey: string; descKey: string }> = [
  { value: 'inline', labelKey: 'property:lob.inline', descKey: 'property:lob.inlineDesc' },
  { value: 'file', labelKey: 'property:lob.file', descKey: 'property:lob.fileDesc' },
  { value: 'skip', labelKey: 'property:lob.skip', descKey: 'property:lob.skipDesc' },
];

const RdbDataPanel: React.FC<Props> = ({ node, onOpenNodeData, nodeDataBusy }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const connections = useSelector((s: RootState) => s.connections.list) as any[];
  const cfg = node?.data?.config || {};
  const connection: string = cfg.connection || '';
  const schema: string = cfg.schema || '';
  const table: string = cfg.table || '';
  const schemas = useSelector((s: RootState) => s.connections.schemaMap[connection]);
  const defaultSchema = useSelector((s: RootState) => s.connections.defaultSchema[connection]);
  // 데이터 정의를 가리키면 연결·스키마·테이블·컬럼은 정의에서 읽는다 — 이 칸들은 읽기 전용 (utils/definitionRefs)
  const defs = useSelector((s: RootState) => s.dataDefinitions.items);
  const linked = !!linkedDefinition(node, defs);

  const patch = useCallback((p: Record<string, any>) => {
    const next = { ...cfg, ...p };
    dispatch(updateNodeData({ id: node.id, data: { config: next, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  const connOptions = useMemo(() => (connections || []).map((c: any) => (typeof c === 'string' ? { connection_id: c } : c)), [connections]);
  const selectedConn = connOptions.find((c: any) => c.connection_id === connection);

  // ── 스키마: 연결을 고르면 목록을 읽는다.
  // 비어 있어도 채우지 않는다 — 빈 스키마는 '연결의 기본 스키마' 로 돌고, 노드를 열었다는 이유로 값을 바꾸면
  // DAG 내용이 달라져 최종 실행 상태가 '실행 뒤 워크플로가 바뀌었다' 로 지워진다(현장 2026-09-17 과 같은 계열).
  useEffect(() => { if (connection && !schemas) dispatch(fetchSchemas(connection)); }, [connection, schemas, dispatch]);

  // ── 컬럼: 테이블이 정해졌는데 그 테이블의 컬럼을 아직 안 읽었으면 읽는다
  const columns: Column[] = useMemo(() => nodeColumns(node), [node]);
  const tableKey = connection && table ? `${connection}|${schema}|${table}` : '';
  const [colState, setColState] = useState<{ loading?: boolean; error?: string }>({});
  const loadColumns = useCallback(async (mode: 'initial' | 'refresh') => {
    if (!connection || !table) return;
    setColState({ loading: true });
    try {
      const res = await fetchTableColumns(connection, table, schema);
      const next: Column[] = (res.columns || []).map((c: any) => ({ name: c.name, dataType: c.dataType, comment: c.comment }));
      const at = new Date().toISOString();
      const p: Record<string, any> = { tableColumns: next, columnsFetchedAt: at, columnsKey: `${connection}|${schema}|${table}` };
      if (mode === 'refresh' && cfg.columnsKey === p.columnsKey) {
        const d = diffColumns(columns, next);
        // 바뀐 게 있을 때만 새로 적는다 — 없으면 예전 기록을 그대로 둔다(아직 확인하지 않은 변경일 수 있다)
        if (hasChanges(d)) p.columnsChange = { at, ...d };
      } else {
        p.columnsChange = undefined;                           // 다른 테이블을 새로 고른 것 — 비교 대상이 아니다
      }
      patch(p);
      setColState({});
    } catch (e: any) {
      setColState({ error: e?.message || t('property:rdb.columnsFailed') });
    }
  }, [connection, table, schema, cfg.columnsKey, columns, patch, t]);
  const loadedFor = useRef('');
  useEffect(() => {
    if (!tableKey || loadedFor.current === tableKey || linked) return;
    loadedFor.current = tableKey;
    // 자료형이 하나도 없으면 다시 읽는다 — 2026-10-30 전에는 자료형을 빠뜨린 채 저장됐다(LOB 를 못 찾았다)
    if (cfg.columnsKey !== tableKey || !columns.length) loadColumns('initial');
    else if (columns.every((c) => !c.dataType)) loadColumns('refresh');   // 같은 테이블 — 아직 확인 안 한 변경 기록은 둔다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  // ── 테이블 이름 자동완성 (모든 스키마)
  const [q, setQ] = useState(table);
  const [suggest, setSuggest] = useState<{ open: boolean; items: FoundTable[]; truncated?: boolean; busy?: boolean; error?: string }>({ open: false, items: [] });
  const typing = useRef(false);
  useEffect(() => { if (!typing.current) setQ(table); }, [table]);
  useEffect(() => {
    if (!typing.current || !connection) return undefined;
    const term = q.trim();
    if (!term) { setSuggest({ open: false, items: [] }); return undefined; }
    const timer = setTimeout(async () => {
      setSuggest((s) => ({ ...s, open: true, busy: true, error: undefined }));
      try {
        const r = await searchTables(connection, term, 30);
        setSuggest({ open: true, items: r.tables, truncated: r.truncated });
      } catch (e: any) {
        setSuggest({ open: true, items: [], error: e?.message || t('property:rdb.searchFailed') });
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [q, connection, t]);

  const chooseTable = (found: FoundTable) => {
    typing.current = false;
    setQ(found.name);
    setSuggest({ open: false, items: [] });
    if (found.name === table && (found.schema || '') === schema) return;
    patch({ schema: found.schema || schema, table: found.name, tableColumns: [], columns: [], columnsFetchedAt: undefined, columnsChange: undefined, columnsKey: undefined });
  };

  const [finderOpen, setFinderOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // ── LOB
  const lobCandidates = columns.filter((c) => isLobType(c.dataType));
  const lobMode: string = cfg.lobMode || 'inline';
  const lobColumns: string[] = Array.isArray(cfg.lobColumns) ? cfg.lobColumns : [];
  const lobDisabled = lobCandidates.length === 0 && lobColumns.length === 0;

  // 캔버스 노드의 '!' 와 같은 기준 — 스키마는 비우면 연결의 기본 스키마라 필수가 아니다
  const missing = [!connection && t('property:rdb.fieldConnection'), !table && t('property:rdb.fieldTable')].filter(Boolean) as string[];

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <PanelHeader typeLabel={t('property:rdb.typeLabel')} right={
        <button type="button" className={smallBtn} disabled={nodeDataBusy} onClick={onOpenNodeData}
          title={t('property:rdb.nodeDataTitle')}>
          {nodeDataBusy ? t('property:rdb.opening') : t('property:rdb.nodeData')}
        </button>
      } />

      <div className="mb-3">
        <FieldLabel>{t('workflow:field.label')}</FieldLabel>
        <input className={inputCls} value={cfg.label ?? node.data?.label ?? ''} onChange={(e) => patch({ label: e.target.value })} placeholder={t('property:rdb.labelPlaceholder')} />
      </div>

      {missing.length > 0 && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          {t('property:rdb.missing', { fields: missing.join(', ') })}
        </div>
      )}

      <DefinitionLinkSection node={node} defType="db" patch={patch} />

      <Section title={t('property:rdb.connection')} required>
        <select className={selectCls} aria-label={t('property:rdb.connection')} value={connection} disabled={linked} title={linked ? t('property:deflink.fromDef') : undefined}
          onChange={(e) => patch({ connection: e.target.value, schema: '', table: '', tableColumns: [], columns: [], columnsFetchedAt: undefined, columnsChange: undefined, columnsKey: undefined })}>
          <option value="">{t('property:rdb.chooseConnection')}</option>
          {connection && !selectedConn && <option value={connection}>{t('property:rdb.notInList', { name: connection })}</option>}
          {connOptions.map((c: any) => <option key={c.connection_id} value={c.connection_id}>{c.connection_id}{c.conn_type ? ` · ${c.conn_type}` : ''}</option>)}
        </select>
        {selectedConn && (
          <div className="mt-1 text-[0.6875rem] text-gray-500 truncate" title={[selectedConn.conn_type, selectedConn.host, selectedConn.login].filter(Boolean).join(' · ')}>
            {[selectedConn.conn_type, selectedConn.host, selectedConn.login && t('property:rdb.account', { login: selectedConn.login })].filter(Boolean).join(' · ')}
          </div>
        )}
      </Section>

      <Section title={t('property:rdb.table')}>
        <div className="mb-2">
          <FieldLabel>{t('property:rdb.schema')} <span className="font-normal text-gray-400">{t('property:rdb.schemaHint')}</span></FieldLabel>
          <select className={selectCls} value={schema} disabled={!connection || linked}
            onChange={(e) => patch({ schema: e.target.value, table: '', tableColumns: [], columns: [], columnsFetchedAt: undefined, columnsChange: undefined, columnsKey: undefined })}>
            <option value="">{connection ? `${t('property:rdb.defaultSchema')}${defaultSchema ? ` (${defaultSchema})` : ''}` : t('property:rdb.pickConnectionFirst')}</option>
            {schema && !(schemas || []).includes(schema) && <option value={schema}>{schema}</option>}
            {(schemas || []).map((s) => <option key={s} value={s}>{s}{s === defaultSchema ? t('property:rdb.defaultMark') : ''}</option>)}
          </select>
        </div>

        <FieldLabel required>{t('property:rdb.table')}</FieldLabel>
        <div className="relative">
          <div className="flex gap-1">
            <input className={inputCls} value={q} disabled={!connection || linked} title={linked ? t('property:deflink.fromDef') : undefined}
              placeholder={connection ? t('property:rdb.tablePlaceholder') : t('property:rdb.pickConnectionFirst')}
              onChange={(e) => { typing.current = true; setQ(e.target.value); }}
              onFocus={(e) => {
                // 칸을 누르면 지금 이름을 통째로 선택한다 — 그대로 치면 새 이름으로 바뀐다(앞에 덧붙지 않게)
                e.target.select();
                if (suggest.items.length) setSuggest((s) => ({ ...s, open: true }));
              }}
              onBlur={() => setTimeout(() => { setSuggest((s) => ({ ...s, open: false })); typing.current = false; setQ(table); }, 150)}
              onKeyDown={(e) => { if (e.key === 'Escape') setSuggest((s) => ({ ...s, open: false })); if (isEnter(e) && suggest.items[0]) chooseTable(suggest.items[0]); }} />
            <button type="button" className={smallBtn} disabled={!connection || linked} onClick={() => setFinderOpen(true)} title={t('property:rdb.browseTitle')}>{t('property:rdb.browse')}</button>
          </div>
          {suggest.open && (
            <div className="absolute z-30 left-0 right-0 mt-0.5 max-h-64 overflow-auto rounded border border-gray-300 bg-white shadow-lg text-xs">
              {suggest.busy && !suggest.items.length ? <div className="px-2 py-1.5 text-gray-500">{t('property:rdb.searching')}</div>
                : suggest.error ? <div className="px-2 py-1.5 text-red-600">{suggest.error}</div>
                  : suggest.items.length === 0 ? <div className="px-2 py-1.5 text-gray-500">{t('property:rdb.noMatch')}</div>
                    : suggest.items.map((item) => (
                      <button key={`${item.schema}.${item.name}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => chooseTable(item)}
                        className="w-full text-left px-2 py-1 hover:bg-blue-50 flex items-baseline gap-2">
                        <span className="font-mono text-gray-900">{item.name}</span>
                        <span className="text-gray-400 font-mono">{item.schema}</span>
                        {item.comment && <span className="text-gray-500 truncate">{item.comment}</span>}
                      </button>
                    ))}
              {suggest.truncated && <div className="px-2 py-1 text-[0.6875rem] text-gray-400 border-t border-gray-100">{t('property:rdb.moreResults')}</div>}
            </div>
          )}
        </div>
        {table && (
          <div className="mt-1 text-[0.6875rem] text-gray-500 font-mono truncate" title={`${schema ? `${schema}.` : ''}${table}`}>
            {t('property:rdb.selected')} {schema ? `${schema}.` : ''}{table}
          </div>
        )}
      </Section>

      <Section title={t('property:rdb.columns')} right={
        <>
          <button type="button" className={smallBtn} disabled={!table || linked || colState.loading} onClick={() => loadColumns('refresh')}
            title={linked ? t('property:deflink.fromDef') : t('property:rdb.refreshTitle')}>{t('property:rdb.refresh')}</button>
          <button type="button" className={smallBtn} disabled={!table} onClick={() => setDetailOpen(true)}>{t('property:rdb.detail')}</button>
        </>
      }>
        {!table ? <div className="text-xs text-gray-400">{t('property:rdb.pickTableFirst')}</div> : (
          <div className="text-xs text-gray-600 space-y-0.5">
            <div>
              {colState.loading ? t('property:rdb.reading') : colState.error ? <span className="text-red-600">{colState.error}</span> : t('property:rdb.columnCount', { n: columns.length })}
              {cfg.columnsFetchedAt && !colState.loading && <span className="text-gray-400">{t('property:rdb.fetchedAt', { at: formatFetchedAt(cfg.columnsFetchedAt) })}</span>}
            </div>
            {cfg.columnsChange && (
              <div className="rounded bg-amber-50 border border-amber-200 px-1.5 py-1 text-[0.6875rem] text-amber-800">
                {t('property:rdb.changedAt', { at: formatFetchedAt(cfg.columnsChange.at) })}
                {cfg.columnsChange.added?.length ? t('property:rdb.added', { cols: cfg.columnsChange.added.join(', ') }) : ''}
                {cfg.columnsChange.removed?.length ? t('property:rdb.removed', { cols: cfg.columnsChange.removed.join(', ') }) : ''}
                {cfg.columnsChange.typeChanged?.length ? t('property:rdb.typeChanged', { cols: cfg.columnsChange.typeChanged.join(', ') }) : ''}
                <div className="text-amber-700/80">{t('property:rdb.changeNote')}</div>
              </div>
            )}
          </div>
        )}
      </Section>

      <Collapsible
        title={t('property:rdb.lobTitle')}
        disabled={lobDisabled}
        defaultOpen={lobMode !== 'inline'}
        summary={lobDisabled ? (table ? t('property:rdb.noLob') : '') : t('property:rdb.lobSummary', { mode: (() => { const m = LOB_MODES.find((x) => x.value === lobMode); return m ? t(m.labelKey) : lobMode; })(), n: lobCandidates.length })}
      >
        <div className="text-[0.6875rem] text-gray-500 mb-1.5">{t('property:rdb.lobIntro')}</div>
        <div className="space-y-0.5 mb-2">
          {LOB_MODES.map((m) => (
            <label key={m.value} className="flex items-start gap-1.5 text-xs text-gray-700 cursor-pointer">
              <input type="radio" className="mt-0.5" name={`lob-${node.id}`} checked={lobMode === m.value} onChange={() => patch({ lobMode: m.value })} />
              <span><span className="font-medium">{t(m.labelKey)}</span> <span className="text-gray-500">— {t(m.descKey)}</span></span>
            </label>
          ))}
        </div>
        <FieldLabel>{t('property:rdb.lobColumns')}</FieldLabel>
        <div className="max-h-32 overflow-auto rounded border border-gray-200 p-1.5 space-y-0.5 mb-2">
          {[...lobCandidates.map((c) => c.name), ...lobColumns.filter((n) => !lobCandidates.some((c) => c.name === n))].map((n) => {
            const col = lobCandidates.find((c) => c.name === n);
            return (
              <label key={n} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={lobColumns.includes(n)}
                  onChange={(e) => patch({ lobColumns: e.target.checked ? [...lobColumns, n] : lobColumns.filter((x) => x !== n) })} />
                <span className="font-mono">{n}</span>
                <span className="text-gray-400 font-mono">{col?.dataType || t('property:rdb.notInTable')}</span>
              </label>
            );
          })}
        </div>
        {lobMode === 'file' && (
          <>
            <FieldLabel>{t('property:rdb.lobDir')}</FieldLabel>
            <input className={inputCls} value={cfg.lobDir ?? 'lob'} onChange={(e) => patch({ lobDir: e.target.value })} placeholder="lob" />
          </>
        )}
      </Collapsible>

      {finderOpen && connection && (
        <TableFinderModal connection={connection} schema={schema} table={table}
          onSelect={(found) => { chooseTable(found); setFinderOpen(false); }}
          onClose={() => setFinderOpen(false)} />
      )}
      {detailOpen && (
        <DataDetailModal
          title={t('property:rdb.detailTitle')}
          subtitle={`${connection} / ${schema ? `${schema}.` : ''}${table}`}
          columns={columns}
          loading={colState.loading}
          error={colState.error}
          preview={{ kind: 'db', connection, schema, table }}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
};

export default RdbDataPanel;
